import isArray from 'lodash/isArray';
import React, { lazy, memo, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Link, matchPath, Redirect, Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';

import { BaseLayout } from 'src/components/common/BaseLayout';
import { CommonSizes } from 'src/components/common/CommonSizes';
import { AdministrationLayout } from 'src/components/common/Layouts/Administration';
import { LayoutMinimal } from 'src/components/common/Layouts/Minimal';
import { Footer } from 'src/components/Footer';
import { Header } from 'src/components/Header';
import { CommonOverlay } from 'src/components/overlays';
import { UiGlobalLoadingIndicator } from 'src/components/UI/GlobalLoadingIndicator';
import { UiInnerNotSPALink } from 'src/components/UI/InnerNotSPALink';
import { UiFullScreenLoader } from 'src/components/UI/loaders/FullScreenTransparent';
import { administrationRoutes } from 'src/pages/Administration/administration';
import { CheckoutConfirmationPage } from 'src/pages/Checkout/Confirmation';
import { CheckoutContributionConfirmationPage } from 'src/pages/Checkout/ContributionConfirmation';
import { DonationPageList } from 'src/pages/DonationPageList';
import { ErrorPage } from 'src/pages/ErrorPage';
import { FeatureFlagPage } from 'src/pages/FrontendFeatureFlags';
import { OrganizationOldVerifyPage } from 'src/pages/Organization/OldVerify';
import { testRoutes } from 'src/routes/test';
import { organizationRoutes, ROUTE_ORGANIZATION_OLD_VERIFY } from 'src/routes/organization';
import { startGuideRoutes } from 'src/routes/startGuide';
import { reportRoutes } from 'src/routes/report';
import { changeSagaType } from 'src/store/rootSaga';
import { getDOMElement } from 'src/utils/DOM';
import { FeatureFlagCheckRoute } from 'src/utils/featureFlagCheckRoute';
import { SafeAnyType } from 'src/utils/safeAny';
import { featureFlagsByFlagSelector } from 'src/store/featureFlags/selectors';
import { currentUserSelector } from 'src/store/user/selectors';
import { changePageTitle } from 'src/utils/hooks/usePageTitle';
import { PlusSubscriptionModals } from 'src/components/plusSubscription/modals';
import { PlusSubscriptionUpgradePage } from 'src/pages/PlusSubscription/Upgrade';
import { accountOrganizerFlaggedRoutes, accountOrganizerRoutes } from 'src/routes/account/organizer';
import { shouldRedirectToStopPageSelector } from 'src/store/commonPart/selector';
import { submissionProgressRoutes } from './routes/submitProgress';

/** lazy loaded paths (only pages) */
const DonationPageGuardedRoute = lazy(() => import('src/pages/DonationSetup/containers/routeGuard'));
const ResetPasswordPage = lazy(() => import('src/pages/ResetPassword'));
const SignInPage = lazy(() => import('src/pages/Account/Profile/Signin'));
const ConfirmEmailPage = lazy(() => import('src/pages/Account/Profile/ConfirmEmail'));
const SettingsStaffPage = lazy(() => import('src/pages/Settings/Staff'));
const UserPersonalSetupFundingPage = lazy(() => import('src/pages/UserPersonal/SetupFunding'));
const StartPage = lazy(() => import('src/pages/StartPages/Start'));
const StartPlusPage = lazy(() => import('src/pages/StartPages/StartPlus'));
const MyCampaignsPage = lazy(() => import('src/pages/Campaigns'));
const SupportersPage = lazy(() => import('src/pages/Supporters'));
const CustomerTypePage = lazy(() => import('src/pages/CustomerType'));
const MyAccountPage = lazy(() => import('src/pages/Account/MyAccount'));
const StopPage = lazy(() => import('src/pages/Stop'));
const EmailMarketingPage = lazy(() => import('src/pages/EmailMarketing'));
const PlusOrganizationSetupPage = lazy(() => import('src/pages/PlusOrganizationSetup'));
const EventRegistrationPage = lazy(() => import('src/pages/EventRegistration/Registration'));
const EventRegistrationInvitationPage = lazy(() => import('src/pages/EventRegistration/Invitation'));
const PledgeFundingPage = lazy(() => import('src/pages/PledgeFunding'));
const PeerToPeerRegistrationPage = lazy(() => import('src/pages/PeerToPeerRegistration'));
const LiveDisplayPage = lazy(() => import('src/pages/LiveDisplay'));

/** end lazy loaded paths */

export type CRoute = {
  path: string | string[];
  Component: React.ComponentType;

  exact?: boolean; // set false if needed
  name?: string;
  allowAnonymous?: boolean;
  layout?: 'minimal' | 'administration' | 'base';
  layoutClassName?: string;
  showFooter?: boolean;

  liveStreamBannerAvailable?: boolean; // moved from window.liveStreamCampaignInfo
  testModeBannerAvailable?: boolean; // moved from CanBeShownTestModeBanner and window.shouldShowTestModeBanner
};

export type CRoutes = CRoute[];

export const ROUTE_SIGNIN_PATH = '/account/profile/signin';
export const ROUTE_CONFIRM_EMAIL = '/account/profile/confirmemail';
export const ROUTE_DONATION_PAGE_SETUP_PATH = '/campaigns/donationpagesetup/:id/:mode?';

export const ROUTE_CAMPAIGN_SETUP_BASE = '/campaigns/setup';

// campaign page
// export const ROUTE_CAMPAIGN_DETAILS_2_PAGE = '/:url/:mode/:tab?';
export const ROUTE_CAMPAIGN_DETAILS_PAGE = '/:id/:mode/details'; // '/:host/:url/:mode/:tab?'

export const ROUTE_CAMPAIGN_PAGE_MEMBER = '/:host/m/:url/:mode?';
export const ROUTE_CAMPAIGN_PAGE_TEAM = '/:host/t/:url/:mode?';
export const ROUTE_CAMPAIGN_AUTOAPPROVE = '/campaigns/autoapprove/:permanenturl';
// end campaign page

export const ROUTE_ORGANIZER_CAMPAIGNS = '/campaigns';
export const ROUTE_DONATION_PAGE_LIST = '/donationpages/:id';
export const ROUTE_START_GUIDE_ORGANIZATION = '/org';
export const ROUTE_START_GUIDE_ADVOCATE = '/adv';
export const ROUTE_START_GUIDE_CSR = '/csr';
export const ROUTE_START_GUIDE_PERSONAL = '/personal';
export const ROUTE_START_GUIDE_EVERGREEN = '/evergreen/onboarding';
export const ROUTE_START_GUIDE_PLUS_SUBSCRIPTION = '/plus/onboarding';
export const ROUTE_EVERGREEN_UPGRADE = '/evergreen/upgrade';
export const ROUTE_PLUS_SUBSCRIPTION_UPGRADE = '/plus/upgrade';
export const ROUTE_START = '/start';
export const ROUTE_START_WITH_ID = `${ROUTE_START}/:id?`;
export const ROUTE_PLUS_START = '/plus/start';
export const ROUTE_PLUS_START_WITH_ID = '/plus/startbyid/:id';
export const ROUTE_EVERGREEN_START = '/evergreen/start';
export const ROUTE_CUSTOMER_TYPE = '/customertype';

export const ROUTE_SUPPORTERS = '/supporters/:permanenturl?';

export const ROUTE_STOP_PAGE = '/stop';

export const ROUTE_INVITATION = '/invitation';
export const ROUTE_INVITATION_PAGE = `${ROUTE_INVITATION}/:permanentUrl`;
export const ROUTE_REGISTRATION_PAGE = '/registration/:id';
export const ROUTE_MY_ACCOUNT_PAGE = '/account/myaccount';

export const ROUTE_PEER_TO_PEER_REGISTRATION = '/PeerToPeerRegistration/:contributionGuid';
export const ROUTE_LIVE_DISPLAY = '/LiveDisplay/:permanentUrl';

export const ROUTE_CAMPAIGN = '/:permanentUrl';

export const MyAccountLink = ({
  text,
  addUrl,
  ...other
}: Record<SafeAnyType, SafeAnyType> & { text: string; addUrl?: string }) => {
  const hasReact = getDOMElement('#r-body-wrapper');

  if (hasReact) {
    return (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <Link to={`${ROUTE_MY_ACCOUNT_PAGE}${addUrl || ''}`} {...other}>
        {text}
      </Link>
    );
  }
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <UiInnerNotSPALink href={`${ROUTE_MY_ACCOUNT_PAGE}${addUrl || ''}`} {...other}>
      {text}
    </UiInnerNotSPALink>
  );
};

export type FlaggedRouteType = {
  path: string | string[];
  flag: string;
  component: React.ComponentType<SafeAnyType>;
  title: string;
} & Pick<CRoute, 'layout' | 'allowAnonymous' | 'showFooter' | 'liveStreamBannerAvailable' | 'testModeBannerAvailable'>;

/** the whole info in one place */
export const FlaggedRoutes: Record<string, FlaggedRouteType> = {
  ROUTE_PLUS_ORGANIZATION_SETUP: {
    path: '/PlusOrganizationSetupPage/:id?',
    flag: 'PlusOrganizationSetupPage',
    component: PlusOrganizationSetupPage,
    title: 'Organization',
  },
  ...accountOrganizerFlaggedRoutes,
} as const;

export const getFlagByRoute = (route: string): string => {
  const values = Object.values(FlaggedRoutes);

  const checkFn = (path: string) => path.toLowerCase() === route.toLowerCase();

  for (let i = 0; i < values.length; i += 1) {
    const item = values[i];
    const paths = isArray(item.path) ? item.path : [item.path];

    for (let j = 0; j < paths.length; j += 1) {
      if (checkFn(paths[j])) {
        return item.flag;
      }
    }
  }

  return '';
};
/** end administration */

/** collect all the routes here to do some checks later */
export const routes: CRoutes = [
  ...Object.entries(FlaggedRoutes).map(([, data]) => ({
    ...data,
    Component: () => <FeatureFlagCheckRoute routeInfo={data} />,
    exact: true,
    allowAnonymous: data.allowAnonymous || false,
  })),
  ...organizationRoutes,
  ...accountOrganizerRoutes,
  ...startGuideRoutes,
  ...testRoutes,
  ...reportRoutes,
  ...submissionProgressRoutes,
  {
    path: ROUTE_SIGNIN_PATH,
    Component: SignInPage,
    allowAnonymous: true,
    layout: 'base',
    layoutClassName: 'LoginPageBackground',
  },
  {
    path: '/resetpassword/:id',
    Component: ResetPasswordPage,
    allowAnonymous: true,
    layout: 'base',
  },
  {
    path: ROUTE_CONFIRM_EMAIL,
    Component: ConfirmEmailPage,
    allowAnonymous: true,
    layout: 'base',
    layoutClassName: 'LoginPageBackground',
  },
  {
    path: '/featuretoggle',
    Component: FeatureFlagPage,
    allowAnonymous: true,
  },
  {
    path: ROUTE_DONATION_PAGE_SETUP_PATH,
    Component: DonationPageGuardedRoute,
    exact: true,
  },
  {
    path: ROUTE_ORGANIZER_CAMPAIGNS,
    Component: MyCampaignsPage,
    exact: true,
  },
  {
    path: ROUTE_DONATION_PAGE_LIST,
    Component: DonationPageList,
    exact: true,
  },
  {
    path: '/userPersonal/setupFunding/:permanenturl?',
    Component: UserPersonalSetupFundingPage,
    exact: true,
  },
  {
    path: ROUTE_EVERGREEN_UPGRADE,
    Component: PlusSubscriptionUpgradePage,
    exact: true,
  },
  {
    path: ROUTE_PLUS_SUBSCRIPTION_UPGRADE,
    Component: PlusSubscriptionUpgradePage,
    exact: true,
  },
  {
    path: '/settings/staff/:organizationurl?',
    Component: SettingsStaffPage,
    exact: true,
  },
  {
    path: '/checkout/confirmation/:id',
    Component: CheckoutConfirmationPage,
    exact: true,
    allowAnonymous: true,
  },
  {
    path: '/checkout/contributionconfirmation/:id',
    Component: CheckoutContributionConfirmationPage,
    exact: true,
    allowAnonymous: true,
  },
  {
    path: ROUTE_START_WITH_ID,
    Component: StartPage,
    allowAnonymous: true,
    layout: 'minimal',
    exact: true,
  },
  {
    path: [ROUTE_PLUS_START, ROUTE_PLUS_START_WITH_ID],
    Component: StartPlusPage,
    allowAnonymous: true,
    layout: 'minimal',
  },
  {
    path: ROUTE_STOP_PAGE,
    Component: StopPage,
    showFooter: false,
    exact: true,
  },
  {
    path: ROUTE_CUSTOMER_TYPE,
    Component: CustomerTypePage,
    exact: true,
    layout: 'base',
  },
  {
    path: ROUTE_SUPPORTERS,
    Component: SupportersPage,
    exact: true,
    layout: 'base',
  },
  {
    path: ROUTE_INVITATION_PAGE,
    Component: EventRegistrationInvitationPage,
    exact: true,
    allowAnonymous: true,
    showFooter: false,
    layout: 'base',
  },
  {
    path: ROUTE_REGISTRATION_PAGE,
    Component: EventRegistrationPage,
    exact: true,
    allowAnonymous: true,
    showFooter: false,
    layout: 'base',
  },
  {
    path: ROUTE_MY_ACCOUNT_PAGE,
    Component: MyAccountPage,
    exact: true,
  },
  {
    path: '/PledgeFunding',
    Component: PledgeFundingPage,
    exact: true,
  },
  {
    path: '/emailmarketing/:id?',
    Component: EmailMarketingPage,
    exact: true,
  },
  {
    path: ROUTE_ORGANIZATION_OLD_VERIFY,
    Component: OrganizationOldVerifyPage,
    exact: true,
  },
  {
    path: ROUTE_PEER_TO_PEER_REGISTRATION,
    Component: PeerToPeerRegistrationPage,
    exact: true,
    showFooter: false,
    allowAnonymous: true,
  },
  {
    path: ROUTE_LIVE_DISPLAY,
    Component: LiveDisplayPage,
    exact: true,
    showFooter: false,
    allowAnonymous: true,
    layout: 'minimal',
    liveStreamBannerAvailable: false,
    testModeBannerAvailable: false,
  },
];

export const RouteTitle: React.FC<{ path: string | undefined }> = memo(({ path }) => {
  useEffect(() => {
    if (path) {
      /** let it be calculated every time. no cache needed */
      const pageTitles = Object.entries(FlaggedRoutes).reduce<Record<string, string>>((acc, [, item]) => {
        if (isArray(item.path)) {
          item.path.forEach((onePath) => {
            acc[onePath] = item.title;
          });
        } else {
          acc[item.path] = item.title;
        }

        return acc;
      }, {});

      const title = path && pageTitles[path];

      if (title) {
        changePageTitle(title);
      }
    }
  }, [path]);

  return null;
});

type Props = {
  errorPageMessage?: string | null;
};

/** there is no check if a user goes to other paths, except routes */
export const RoutesChecker: React.FC<Props> = ({ errorPageMessage }) => {
  const { current: allRoutes } = useRef(
    [...Object.values(routes), ...Object.values(administrationRoutes)].map(({ path }) => path).flat()
  );

  /** check if a route matches the current path */
  const { path } = useRouteMatch({ path: allRoutes, exact: true }) || {};

  const { pathname } = useLocation();

  const flag = getFlagByRoute(path || '');

  const allowed = useSelector(featureFlagsByFlagSelector(flag));

  /** for pages that are already SPA but have no flag any more, excluding non-SPA pages */
  const found = flag ? allowed : !!path;

  const { response: userExists } = useSelector(currentUserSelector);

  const preparedRoutes = useMemo(
    () => (userExists ? routes : routes.filter(({ allowAnonymous }) => allowAnonymous)),
    [userExists]
  );

  const currentRouteInfo = useMemo(
    () =>
      [...routes, ...administrationRoutes].find((item) =>
        isArray(item.path) ? item.path.includes(path || '') : item.path === path
      ),
    [path]
  );

  /** the flag showing if the App was restarted (changed layout minimal <-> full) */
  const restartedRef = useRef<boolean | null>(null);

  const appRestartedAndMustUseBaseLayout: boolean =
    restartedRef.current === true && (!currentRouteInfo?.layout || currentRouteInfo?.layout !== 'minimal');

  /** define here pages which don't need to have the base layout */
  const forceMinimalLayoutOnSomePages: boolean =
    currentRouteInfo?.layout === undefined && !!matchPath(pathname, [ROUTE_PLUS_START, ROUTE_EVERGREEN_START]);

  const hasBaseLayout = currentRouteInfo?.layout === 'base';
  const hasMinimalLayout = currentRouteInfo?.layout === 'minimal' || forceMinimalLayoutOnSomePages;
  const hasAdministrationLayout = currentRouteInfo?.layout === 'administration';

  const stopPaths = [
    ROUTE_ORGANIZER_CAMPAIGNS,
    ROUTE_SUPPORTERS,
    '/emailMarketing/:id?',
    '/organization/*',
    '/settings/*',
    '/userPersonal/*',
    ROUTE_START_GUIDE_PERSONAL,
    ROUTE_START_GUIDE_ORGANIZATION,
  ];

  const shouldRedirectToStopPage = useSelector(shouldRedirectToStopPageSelector);

  /**
   * there are two different layouts - full and minimal
   * so, we need to toggle them when the pathname changes
   */
  useEffect(() => {
    changeSagaType(hasMinimalLayout ? 'minimal' : 'full');

    /**
     * initial                  - null
     * 1 render                 - false
     * 2 render and any other   - true
     */
    restartedRef.current = restartedRef.current !== null;
  }, [hasMinimalLayout]);

  if (hasMinimalLayout) {
    return found ? (
      <LayoutMinimal path={path}>
        <Switch>
          {shouldRedirectToStopPage && (
            <Route path={stopPaths} exact>
              <Redirect to={ROUTE_STOP_PAGE} />
            </Route>
          )}
          {routes
            .filter(({ layout }) => layout === 'minimal')
            .map(({ path, Component, exact = false }) => (
              <Route key={path.toString()} path={path} exact={exact}>
                <Component />
              </Route>
            ))}
        </Switch>
      </LayoutMinimal>
    ) : null;
  }

  if (hasAdministrationLayout) {
    return found ? <AdministrationLayout path={path} shouldUseBaseLayout={appRestartedAndMustUseBaseLayout} /> : null;
  }

  /** a case of the error page */
  if (errorPageMessage !== null && errorPageMessage !== undefined) {
    return (
      <BaseLayout>
        <ErrorPage message={errorPageMessage} />
        <Footer />
      </BaseLayout>
    );
  }

  /** if the property set or doesn't even exist */
  const showFooter = currentRouteInfo?.showFooter === true || currentRouteInfo?.showFooter === undefined;

  const Component = (
    <>
      <Header currentRouteInfo={currentRouteInfo} />

      <CommonSizes />

      <UiGlobalLoadingIndicator hasRoutes={found}>
        <RouteTitle path={path} />

        <React.Suspense fallback={<UiFullScreenLoader transparent />}>
          {found ? (
            <Switch>
              {shouldRedirectToStopPage && (
                <Route path={stopPaths} exact>
                  <Redirect to={ROUTE_STOP_PAGE} />
                </Route>
              )}
              {preparedRoutes.map(({ path, Component, exact = false }) => (
                <Route key={path.toString()} path={path} exact={exact}>
                  <Component />
                </Route>
              ))}
            </Switch>
          ) : null}
        </React.Suspense>
      </UiGlobalLoadingIndicator>

      <CommonOverlay />

      <PlusSubscriptionModals />

      {showFooter && <Footer />}
    </>
  );

  return hasBaseLayout || appRestartedAndMustUseBaseLayout ? (
    <BaseLayout className={currentRouteInfo?.layoutClassName}>{Component}</BaseLayout>
  ) : (
    Component
  );
};
